/**
 * The board's stacking order, in one place.
 *
 * Everything on this board scrolls in two axes under two sticky bars, so painting order is a real
 * feature and not a detail: **the sticky chrome always wins.** A block scrolled under the technician
 * column has to vanish behind it — including the badges and handle strips *inside* the block, which
 * is what `isolate` on `ScheduleBlock` is for. Without that isolation a `z-10` handle strip is not
 * "10 within its block", it is 10 against the whole page, and it ties with the sticky column.
 *
 * Tailwind classes rather than numbers because every consumer is a `className`. They stay under the
 * `Modal` shell's `z-index: 100` and the burger menu's `z-[200]`, so an open form always covers the
 * board.
 */
export const BOARD_LAYER = {
  /**
   * Full-height markers over the grid — currently only the red now-line. Above every block, below
   * the technician column, because the column is chrome and the marker belongs to the time grid.
   */
  marker: 'z-10',

  /**
   * The drag ghost and the planning draft. Above every block in every row — a drag that moves a
   * block into an earlier row must not slide under that row's blocks — and still behind the chrome.
   */
  ghost: 'z-20',

  /** The sticky technician column of a body row, and its skeleton placeholder. */
  technicianColumn: 'z-30',

  /** The sticky hour axis. Above the column: on vertical scroll it covers it. */
  header: 'z-40',

  /** The header's technician cell — sticky in both axes, so it has to beat both sticky neighbours. */
  headerCorner: 'z-50',

  /**
   * `BlockInfoCard`, portaled to `document.body`.
   *
   * Above every piece of board chrome — a card half-hidden behind the hour axis would be worse than
   * no card — and deliberately below the `Modal` shell's 100, so a docked upsert form covers it
   * rather than the other way round. That ordering is why the card treats the docked panel's width
   * as the right-hand edge of the viewport: it cannot win against the panel, so it moves.
   *
   * The one value here that is not a plain Tailwind step, because 90 has to sit between two numbers
   * this file does not own.
   */
  hoverCard: 'z-[90]',
} as const;
